import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { InteractiveCard } from "../../../components/PromotionIQ/InteractiveCard";
import { ScreenHeader } from "../../../components/PromotionIQ/ScreenHeader";
import {
  retailers,
  formatCurrency,
  formatPercent,
  getRetailerById,
} from "../../../data/PromotionIQ/PromotionData";
import { Target, AlertTriangle, CheckCircle, FileText, ChevronRight, TrendingUp, Users } from "lucide-react";
import React from "react";

interface NegotiationBrief {
  retailer_id: string;
  recommended_price_range: { min: number; max: number };
  tool_mix: string[];
  walk_away_threshold: number;
  evidence: string[];
  strengths: string[];
  weaknesses: string[];
}

const Negotiation = () => {
  const navigate = useNavigate();
  const [selectedRetailer, setSelectedRetailer] = useState("R1");
  const retailer = getRetailerById(selectedRetailer);

  // Generate negotiation briefs based on TRINITI Organics retailer data
  const getNegotiationBrief = (retailerId: string): NegotiationBrief => {
    const briefs: Record<string, NegotiationBrief> = {
      R1: {
        retailer_id: "R1",
        recommended_price_range: { min: 10, max: 15 },
        tool_mix: ["Display", "Instagram", "TikTok"],
        walk_away_threshold: 75000,
        evidence: [
          "P101 achieved 1.42x ROI with Display + Instagram at 15% discount",
          "Mediterranean line drove 40% revenue share with social support",
          "92% execution reliability ensures tool investments deliver",
          "$23.4M YTD sales (88% of total) validates premium positioning",
        ],
        strengths: ["Highest ROI retailer (1.42x)", "Premium organic positioning", "Strong social media synergy"],
        weaknesses: ["120-day lock-in limits flexibility", "Higher slotting fees", "Premium pricing expectations"],
      },
      R2: {
        retailer_id: "R2",
        recommended_price_range: { min: 15, max: 20 },
        tool_mix: ["Display", "Feature"],
        walk_away_threshold: 45000,
        evidence: [
          "High elasticity sensitivity supports promotional response",
          "P104-P106 showed pantry loading above 18% discount",
          "Weekly cadence creates forward buying risk",
          "$3.1M YTD sales with strong growth trajectory",
        ],
        strengths: ["High elasticity", "Weekly promo windows", "Health-focused shoppers"],
        weaknesses: ["Lower ROI (1.28x)", "Pantry loading risk", "Smaller store footprint"],
      },
    };

    return briefs[retailerId] || briefs.R1;
  };

  const brief = getNegotiationBrief(selectedRetailer);

  return (
    <div className="space-y-6">
      <ScreenHeader
        title="Retailer Negotiation Prep"
        question="How do I walk into the next meeting?"
      />

      {/* Retailer Selector */}
      <div className="border border-border bg-card p-6">
        <div className="flex items-center justify-between">
          <div>
            <label className="label-athena mb-2 block">Select Retailer</label>
            <select
              value={selectedRetailer}
              onChange={(e) => setSelectedRetailer(e.target.value)}
              className="input-athena max-w-md"
            >
              {retailers.map((r) => (
                <option key={r.retailer_id} value={r.retailer_id}>
                  {r.name} - {r.economic_model}
                </option>
              ))}
            </select>
          </div>
          <button 
            onClick={() => navigate(`/promotioniq/retailer-economics/${selectedRetailer}`)}
            className="btn-athena"
          >
            <Users className="h-4 w-4 mr-2" />
            Full Retailer Profile
            <ChevronRight className="h-4 w-4 ml-1" />
          </button>
        </div>
      </div>

      {/* Key Recommendations */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <InteractiveCard
          label="Recommended Discount"
          value={`${brief.recommended_price_range.min}% - ${brief.recommended_price_range.max}%`}
          subValue="Optimal price position"
          variant="highlight"
          linkTo="/promotioniq/promotion-builder"
          linkLabel="Build promotion"
        />
        <InteractiveCard
          label="Walk-away Threshold"
          value={formatCurrency(brief.walk_away_threshold)}
          subValue="Minimum acceptable spend"
          variant="warning"
        />
        <InteractiveCard
          label="YTD Sales"
          value={formatCurrency(retailer?.ytd_sales || 0)}
          subValue="Account value"
          variant="default"
          linkTo={`/promotioniq/retailer-economics/${selectedRetailer}`}
          linkLabel="View economics"
        />
        <InteractiveCard
          label="ROI Index"
          value={`${retailer?.roi_index.toFixed(2)}x`}
          subValue="vs portfolio average"
          variant={retailer && retailer.roi_index >= 1 ? "success" : "warning"}
          linkTo="/promotioniq/retailer-investment"
          linkLabel="Compare retailers"
        />
      </div>

      {/* Recommended Tool Mix */}
      <div className="border border-border bg-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="section-header">Recommended Tool Mix</h3>
          <button 
            onClick={() => navigate("/promotioniq/shopper-influence")}
            className="text-xs text-primary hover:underline flex items-center gap-1"
          >
            Explore Influence Stack <ChevronRight className="h-3 w-3" />
          </button>
        </div>
        <div className="flex flex-wrap gap-3">
          {brief.tool_mix.map((tool) => (
            <div
              key={tool}
              onClick={() => navigate("/promotioniq/promotion-builder")}
              className="flex items-center gap-2 bg-primary/10 border border-primary/30 px-4 py-3 cursor-pointer hover:bg-primary/20 transition-colors"
            >
              <Target className="h-5 w-5 text-primary" />
              <span className="font-medium">{tool}</span>
            </div>
          ))}
        </div>
        <p className="mt-4 text-sm text-muted-foreground">
          Based on {retailer?.name}'s {retailer?.economic_model} model and historical TRINITI performance
        </p>
      </div>

      {/* Evidence & Analysis */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Evidence */}
        <div className="border border-border bg-card p-6">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="h-5 w-5 text-primary" />
            <h3 className="section-header">Supporting Evidence</h3>
          </div>
          <ul className="space-y-3">
            {brief.evidence.map((item, idx) => (
              <li 
                key={idx} 
                onClick={() => navigate("/promotioniq/retailer-promotion-truth")}
                className="flex items-start gap-2 text-sm cursor-pointer hover:bg-primary/5 p-2 -m-2 transition-colors group"
              >
                <span className="text-primary mt-1">•</span>
                <span className="flex-1">{item}</span>
                <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </li>
            ))}
          </ul>
        </div>

        {/* SWOT-style Analysis */}
        <div className="space-y-4">
          <div className="border border-success/30 bg-success/5 p-4">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle className="h-4 w-4 text-success" />
              <h4 className="font-medium text-success">Strengths</h4>
            </div>
            <ul className="space-y-1">
              {brief.strengths.map((item, idx) => (
                <li key={idx} className="text-sm flex items-center gap-2">
                  <span className="text-success">+</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="border border-warning/30 bg-warning/5 p-4">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="h-4 w-4 text-warning" />
              <h4 className="font-medium text-warning">Considerations</h4>
            </div>
            <ul className="space-y-1">
              {brief.weaknesses.map((item, idx) => (
                <li key={idx} className="text-sm flex items-center gap-2">
                  <span className="text-warning">-</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Retailer Profile Quick Reference */}
      <div 
        onClick={() => navigate(`/promotioniq/retailer-economics/${selectedRetailer}`)}
        className="border border-border bg-card p-6 cursor-pointer hover:border-primary transition-all group"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="section-header">Retailer Quick Reference</h3>
          <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
        </div>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
          <div className="text-center p-3 bg-muted/30">
            <p className="text-xs text-muted-foreground">Economic Model</p>
            <p className="font-medium">{retailer?.economic_model}</p>
          </div>
          <div className="text-center p-3 bg-muted/30">
            <p className="text-xs text-muted-foreground">Promo Cadence</p>
            <p className="font-medium">{retailer?.promo_cadence}</p>
          </div>
          <div className="text-center p-3 bg-muted/30">
            <p className="text-xs text-muted-foreground">Lock-in Window</p>
            <p className="font-medium">{retailer?.lock_in_window_days} days</p>
          </div>
          <div className="text-center p-3 bg-muted/30">
            <p className="text-xs text-muted-foreground">Execution</p>
            <p className="font-medium">{retailer ? formatPercent(retailer.execution_reliability) : "—"}</p>
          </div>
          <div className="text-center p-3 bg-muted/30">
            <p className="text-xs text-muted-foreground">Elasticity</p>
            <p className="font-medium">{retailer?.elasticity_sensitivity}</p>
          </div>
        </div>
      </div>

      {/* Negotiation Talking Points */}
      <div className="border border-primary/50 bg-primary/5 p-6">
        <h3 className="section-header mb-4">Key Talking Points</h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="p-4 bg-card border border-border">
            <p className="font-medium text-primary mb-2">Value Proposition</p>
            <ul className="space-y-1 text-sm">
              <li 
                onClick={() => navigate("/promotioniq/summary")}
                className="flex items-center gap-1 cursor-pointer hover:text-primary"
              >
                • TRINITI Organics delivers +31.3% YoY growth
                <ChevronRight className="h-3 w-3 opacity-50" />
              </li>
              <li className="flex items-center gap-1">• Mediterranean line is #1 in organic frozen category</li>
              <li 
                onClick={() => navigate("/promotioniq/impact-panel")}
                className="flex items-center gap-1 cursor-pointer hover:text-primary"
              >
                • Strong social media following drives store traffic
                <ChevronRight className="h-3 w-3 opacity-50" />
              </li>
            </ul>
          </div>
          <div className="p-4 bg-card border border-border">
            <p className="font-medium text-primary mb-2">Ask Points</p>
            <ul className="space-y-1 text-sm">
              <li className="flex items-center gap-1">• Premium end-cap placement for Mediterranean line</li>
              <li 
                onClick={() => navigate("/promotioniq/shopper-influence")}
                className="flex items-center gap-1 cursor-pointer hover:text-primary"
              >
                • Co-funded Instagram/TikTok campaigns
                <ChevronRight className="h-3 w-3 opacity-50" />
              </li>
              <li className="flex items-center gap-1">• Shorter lock-in window for test promotions</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Related Actions */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div 
          onClick={() => navigate("/promotioniq/learnings")}
          className="border border-border bg-card p-4 cursor-pointer hover:border-primary hover:bg-primary/5 transition-all group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <TrendingUp className="h-5 w-5 text-primary" />
              <div>
                <p className="font-medium group-hover:text-primary transition-colors">Review Learnings</p>
                <p className="text-sm text-muted-foreground">Use evidence from past cycles</p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
        </div>
        <div 
          onClick={() => navigate("/promotioniq/promotion-builder")}
          className="border border-border bg-card p-4 cursor-pointer hover:border-primary hover:bg-primary/5 transition-all group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Target className="h-5 w-5 text-success" />
              <div>
                <p className="font-medium group-hover:text-primary transition-colors">Build Proposal</p>
                <p className="text-sm text-muted-foreground">Create optimized investment</p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
        </div>
        <div 
          onClick={() => navigate("/promotioniq/summary")}
          className="border border-border bg-card p-4 cursor-pointer hover:border-primary hover:bg-primary/5 transition-all group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-warning" />
              <div>
                <p className="font-medium group-hover:text-primary transition-colors">Executive Summary</p>
                <p className="text-sm text-muted-foreground">Export for JBP meetings</p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Negotiation;
